import { useMemo, useState } from 'react';
import Editor from '@monaco-editor/react';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { ConfigFieldDescription } from './ConfigFieldDescription';
import { useTheme } from '../../contexts/ThemeContext';

interface JsonConfigFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  description?: string;
}

/** Return a JSON parse error message, or null if valid (or empty). */
function getJsonError(text: string): string | null {
  // Empty is allowed — these JSON config fields are typically optional.
  if (!text || !text.trim()) return null;
  try {
    JSON.parse(text);
    return null;
  } catch (e) {
    return e instanceof Error ? e.message : 'Invalid JSON';
  }
}

/**
 * A config field that edits a JSON string in a Monaco editor (syntax
 * highlighting, a Format button, and live validation) instead of a plain
 * textarea. The stored value is still the raw JSON string.
 */
export function JsonConfigField({ label, value, onChange, description }: JsonConfigFieldProps) {
  const { theme } = useTheme();
  const [touched, setTouched] = useState(false);
  const error = useMemo(() => getJsonError(value || ''), [value]);

  const handleFormat = () => {
    try {
      onChange(JSON.stringify(JSON.parse(value || ''), null, 2));
    } catch {
      // Button is disabled while invalid; ignore.
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs"
          disabled={!!error || !value?.trim()}
          onClick={handleFormat}
        >
          Format
        </Button>
      </div>
      <div className="rounded-md border overflow-hidden">
        <Editor
          height="220px"
          defaultLanguage="json"
          value={value || ''}
          onChange={(v) => { setTouched(true); onChange(v ?? ''); }}
          theme={theme === 'dark' ? 'vs-dark' : 'vs'}
          options={{
            minimap: { enabled: false },
            fontSize: 13,
            wordWrap: 'on',
            automaticLayout: true,
            scrollBeyondLastLine: false,
            lineNumbers: 'on',
            tabSize: 2,
            formatOnPaste: true,
            // Let the page scroll when the editor isn't focused/hovered.
            scrollbar: { alwaysConsumeMouseWheel: false },
          }}
        />
      </div>
      {touched && error && (
        <p className="text-xs text-destructive">Invalid JSON: {error}</p>
      )}
      <ConfigFieldDescription description={description} />
    </div>
  );
}
